import React, { useState, useEffect, useMemo } from 'react';
import { 
  Button 
} from '../components/ui/button';
import { 
  Input 
} from '../components/ui/input';
import { 
  Label 
} from '../components/ui/label';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { 
  Badge 
} from '../components/ui/badge';
import { 
  Separator 
} from '../components/ui/separator';
import { 
  Plus, 
  Edit2, 
  Trash2 
} from 'lucide-react';
import ReactPaginate from 'react-paginate';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api/`;
const ITEMS_PER_PAGE = 10;

const WindFarmManagement = () => {
  // Turbine States
  const [turbines, setTurbines] = useState([]);
  const [turbinesLoading, setTurbinesLoading] = useState(false);
  const [turbinesError, setTurbinesError] = useState(null);

  // Modal States
  const [showAddTurbineModal, setShowAddTurbineModal] = useState(false);
  const [showEditTurbineDetailModal, setShowEditTurbineDetailModal] = useState(false);

  // Selected Turbine
  const [selectedTurbine, setSelectedTurbine] = useState(null);

  // New Turbine State
  const [newTurbine, setNewTurbine] = useState({
    name: '',
    nameId: '',
    height: '',
    model: '',
    power: '',
    type: '',
    manufacturer: '',
    typeId: '',
  });

  // Search and Pagination States
  const [searchTurbineList, setSearchTurbineList] = useState('');
  const [currentTurbineListPage, setCurrentTurbineListPage] = useState(0);

  // Function to retrieve the auth token
  const getAuthToken = () => {
    return localStorage.getItem('access_token');
};

  // Function to fetch turbines from API
  const fetchTurbines = async () => {
    setTurbinesLoading(true);
    setTurbinesError(null);
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('No authentication token found. Please log in.');
      }

      const response = await fetch(`${API_BASE_URL}/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Fetch Response Status:', response.status);

      if (response.status === 401) {
        throw new Error('Unauthorized. Please log in.');
      }

      if (response.status === 404) {
        throw new Error('API endpoint not found. Please check the URL.');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch turbines.');
      }

      const data = await response.json();
      console.log('Fetched Turbines Data:', data); // Debugging statement

      // Handle different response structures
      if (Array.isArray(data)) {
        setTurbines(data);
      } else if (data.results && Array.isArray(data.results)) {
        setTurbines(data.results);
      } else {
        throw new Error('Unexpected data format received from the API.');
      }
    } catch (error) {
      console.error('Error fetching turbines:', error);
      setTurbinesError(error.message);
    } finally {
      setTurbinesLoading(false);
    }
  };

  useEffect(() => {
    fetchTurbines();
  }, []);

  // Function to handle adding a new Turbine
  const handleAddTurbine = async () => {
    const { name, nameId, height, model, power, type, manufacturer, typeId } = newTurbine;
    if (!name || !nameId || !height || !model || !power || !type || !manufacturer || !typeId) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch('/api/turbine/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newTurbine)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add turbine.');
      }

      const addedTurbine = await response.json();
      console.log('Added Turbine:', addedTurbine);
      alert('Turbine added successfully.');

      // Reset form and close modal
      setNewTurbine({
        name: '',
        nameId: '',
        height: '',
        model: '',
        power: '',
        type: '',
        manufacturer: '',
        typeId: '',
      });
      setShowAddTurbineModal(false);
      fetchTurbines(); // Refresh the turbines list
    } catch (error) {
      console.error('Error adding turbine:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Function to handle deleting a Turbine
  const handleDeleteTurbine = async (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this turbine?');
    if (!confirmDelete) return;

    try {
      const token = getAuthToken();
      const response = await fetch(`/api/turbine/${id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 204) {
        setTurbines(turbines.filter(t => t.id !== id));
        alert('Turbine deleted successfully.');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete turbine.');
      }
    } catch (error) {
      console.error('Error deleting turbine:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Function to handle editing turbine details
  const handleEditTurbineDetails = async () => {
    const { id, name, nameId, height, model, power, type, manufacturer, typeId } = selectedTurbine;
    if (!name || !nameId || !height || !model || !power || !type || !manufacturer || !typeId) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      const token = getAuthToken();
      const response = await fetch(`/api/turbine/${id}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(selectedTurbine)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update turbine.');
      }

      const updatedTurbine = await response.json();
      setTurbines(turbines.map(t => t.id === updatedTurbine.id ? updatedTurbine : t));
      setShowEditTurbineDetailModal(false);
      alert('Turbine updated successfully.');
    } catch (error) {
      console.error('Error updating turbine:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Derived data for pagination and search in Turbines
  const filteredTurbines = useMemo(() => {
    return turbines.filter(turbine =>
      turbine.name.toLowerCase().includes(searchTurbineList.toLowerCase()) ||
      turbine.nameId.toLowerCase().includes(searchTurbineList.toLowerCase()) ||
      turbine.model.toLowerCase().includes(searchTurbineList.toLowerCase()) ||
      turbine.type.toLowerCase().includes(searchTurbineList.toLowerCase()) ||
      turbine.manufacturer.toLowerCase().includes(searchTurbineList.toLowerCase())
    );
  }, [turbines, searchTurbineList]);

  const paginatedTurbinesList = useMemo(() => {
    const start = currentTurbineListPage * ITEMS_PER_PAGE;
    return filteredTurbines.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTurbines, currentTurbineListPage]);

  const pageCountTurbines = Math.ceil(filteredTurbines.length / ITEMS_PER_PAGE);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Wind Farm Management</h1>
          <p className="mt-2 text-gray-600">Manage your wind turbines efficiently</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">
        {/* Turbines Section */}
        <section>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Turbines</h2>
              <p className="text-gray-600">Manage individual turbines</p>
            </div>
            <Button 
              onClick={() => setShowAddTurbineModal(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Turbine
            </Button>
          </div>

          {/* Search Field */}
          <div className="mb-4">
            <Input
              placeholder="Search Turbines..."
              value={searchTurbineList}
              onChange={(e) => setSearchTurbineList(e.target.value)}
              className="w-full max-w-md"
            />
          </div>

          {/* Display Loading and Error States */}
          {turbinesLoading && <p className="text-gray-500">Loading turbines...</p>}
          {turbinesError && <p className="text-red-500">{turbinesError}</p>}

          {/* Turbines Table */}
          {!turbinesLoading && !turbinesError && (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white shadow rounded-lg" aria-label="Turbines Table">
                <thead className="bg-gray-100">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-medium text-gray-700">Name</th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-medium text-gray-700">Name ID</th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-medium text-gray-700">Height (m)</th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-medium text-gray-700">Model</th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-medium text-gray-700">Power (MW)</th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-medium text-gray-700">Type</th>
                    <th scope="col" className="px-4 py-2 text-left text-sm font-medium text-gray-700">Manufacturer</th>
                    <th scope="col" className="px-4 py-2 text-right text-sm font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedTurbinesList.map((turbine) => (
                    <tr key={turbine.id}>
                      <td className="px-4 py-2">{turbine.name}</td>
                      <td className="px-4 py-2">{turbine.nameId}</td>
                      <td className="px-4 py-2">{turbine.height}</td>
                      <td className="px-4 py-2">{turbine.model}</td>
                      <td className="px-4 py-2">{turbine.power}</td>
                      <td className="px-4 py-2">{turbine.type}</td>
                      <td className="px-4 py-2">{turbine.manufacturer}</td>
                      <td className="px-4 py-2 text-right space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedTurbine(turbine);
                            setShowEditTurbineDetailModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-700"
                          aria-label={`Edit ${turbine.name}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTurbine(turbine.id)}
                          className="text-red-600 hover:text-red-700"
                          aria-label={`Delete ${turbine.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {paginatedTurbinesList.length === 0 && (
                    <tr>
                      <td colSpan="8" className="px-4 py-2 text-center text-gray-500">
                        No Turbines Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pageCountTurbines > 1 && (
            <div className="flex justify-center mt-4">
              <ReactPaginate
                previousLabel={'← Previous'}
                nextLabel={'Next →'}
                pageCount={pageCountTurbines}
                onPageChange={({ selected }) => setCurrentTurbineListPage(selected)}
                containerClassName={'flex space-x-2'}
                previousLinkClassName={'px-3 py-1 border rounded'}
                nextLinkClassName={'px-3 py-1 border rounded'}
                activeLinkClassName={'bg-blue-500 text-white'}
                disabledLinkClassName={'opacity-50 cursor-not-allowed'}
              />
            </div>
          )}
        </section>
      </div>

      {/* Modal for Adding Turbine */}
      <Dialog open={showAddTurbineModal} onOpenChange={setShowAddTurbineModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add New Turbine</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Name</Label>
              <Input
                value={newTurbine.name}
                onChange={(e) => setNewTurbine({ ...newTurbine, name: e.target.value })}
                placeholder="e.g. Turbine A"
              />
            </div>
            <div>
              <Label>Name ID</Label>
              <Input
                value={newTurbine.nameId}
                onChange={(e) => setNewTurbine({ ...newTurbine, nameId: e.target.value })}
                placeholder="e.g. 00001"
              />
            </div>
            <div>
              <Label>Height (m)</Label>
              <Input
                type="number"
                value={newTurbine.height}
                onChange={(e) => setNewTurbine({ ...newTurbine, height: parseInt(e.target.value) })}
                placeholder="e.g. 100"
              />
            </div>
            <div>
              <Label>Model</Label>
              <Input
                value={newTurbine.model}
                onChange={(e) => setNewTurbine({ ...newTurbine, model: e.target.value })}
                placeholder="e.g. V90-2.0 MW"
              />
            </div>
            <div>
              <Label>Power (MW)</Label>
              <Input
                type="number"
                step="0.1"
                value={newTurbine.power}
                onChange={(e) => setNewTurbine({ ...newTurbine, power: parseFloat(e.target.value) })}
                placeholder="e.g. 2.0"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Input
                value={newTurbine.type}
                onChange={(e) => setNewTurbine({ ...newTurbine, type: e.target.value })}
                placeholder="e.g. Offshore"
              />
            </div>
            <div>
              <Label>Manufacturer</Label>
              <Input
                value={newTurbine.manufacturer}
                onChange={(e) => setNewTurbine({ ...newTurbine, manufacturer: e.target.value })}
                placeholder="e.g. Vestas"
              />
            </div>
            <div>
              <Label>Turbine Type ID</Label>
              <Input
                type="number"
                value={newTurbine.typeId}
                onChange={(e) => setNewTurbine({ ...newTurbine, typeId: parseInt(e.target.value) })}
                placeholder="Turbine Type ID"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTurbineModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddTurbine}>
              Add Turbine
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal for Editing Turbine Details */}
      {selectedTurbine && (
        <Dialog open={showEditTurbineDetailModal} onOpenChange={setShowEditTurbineDetailModal}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Edit Turbine {selectedTurbine.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Name</Label>
                  <Input
                    value={selectedTurbine.name}
                    onChange={(e) => setSelectedTurbine({ ...selectedTurbine, name: e.target.value })}
                    placeholder="e.g. Turbine A"
                  />
                </div>
                <div>
                  <Label>Name ID</Label>
                  <Input
                    value={selectedTurbine.nameId}
                    onChange={(e) => setSelectedTurbine({ ...selectedTurbine, nameId: e.target.value })}
                    placeholder="e.g. 00001"
                  />
                </div>
                <div>
                  <Label>Height (m)</Label>
                  <Input
                    type="number"
                    value={selectedTurbine.height}
                    onChange={(e) => setSelectedTurbine({ ...selectedTurbine, height: parseInt(e.target.value) })}
                    placeholder="e.g. 100"
                  />
                </div>
                <div>
                  <Label>Model</Label>
                  <Input
                    value={selectedTurbine.model}
                    onChange={(e) => setSelectedTurbine({ ...selectedTurbine, model: e.target.value })}
                    placeholder="e.g. V90-2.0 MW"
                  />
                </div>
                <div>
                  <Label>Power (MW)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={selectedTurbine.power}
                    onChange={(e) => setSelectedTurbine({ ...selectedTurbine, power: parseFloat(e.target.value) })}
                    placeholder="e.g. 2.0"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Input
                    value={selectedTurbine.type}
                    onChange={(e) => setSelectedTurbine({ ...selectedTurbine, type: e.target.value })}
                    placeholder="e.g. Offshore"
                  />
                </div>
                <div>
                  <Label>Manufacturer</Label>
                  <Input
                    value={selectedTurbine.manufacturer}
                    onChange={(e) => setSelectedTurbine({ ...selectedTurbine, manufacturer: e.target.value })}
                    placeholder="e.g. Vestas"
                  />
                </div>
                <div>
                  <Label>Turbine Type ID</Label>
                  <Input
                    type="number"
                    value={selectedTurbine.typeId}
                    onChange={(e) => setSelectedTurbine({ ...selectedTurbine, typeId: parseInt(e.target.value) })}
                    placeholder="Turbine Type ID"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditTurbineDetailModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleEditTurbineDetails}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default WindFarmManagement;
